import { Button, Grid, LinearProgress, Stack, Typography } from "@mui/material";
import omit from "lodash/omit";
import type { FC } from "react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import AsyncLoadingButton from "../../components/asyncLoadingButton/AsyncLoadingButton";
import CancelButton from "../../components/cancelButton/CancelButton";
import ConfirmationDialog from "../../components/confirmationDialog/ConfirmationDialog";
import type { ProviderFormState } from "./ProviderEdit";
import ProviderEdit from "./ProviderEdit";
import ProviderFiles from "./ProviderFiles";
import type { CreateOrUpdateProviderRequest, ProviderResponse } from "./providersApi";
import { useProvider } from "./useProvider";

interface Props {
  readonly existingProvider: ProviderResponse;
  readonly onUpdateProvider: (request: CreateOrUpdateProviderRequest) => Promise<ProviderResponse>;
  readonly onDeleteProvider: () => Promise<void>;
}

const EditProviderPage: FC<Props> = ({ existingProvider, onUpdateProvider, onDeleteProvider }) => {
  const navigate = useNavigate();
  const [confirmationDialogVisible, setConfirmationDialogVisible] = useState(false);
  const { control, handleSubmit } = useForm<ProviderFormState>({
    defaultValues: {
      ...omit(existingProvider, "id", "files", "listTemplate"),
      taxInfo: {
        ...existingProvider.taxInfo,
        skipValidation: false,
      },
    },
  });

  const goBack = () => navigate("/admin/providers");

  const handleDelete = async () => {
    await onDeleteProvider();
    goBack();
  };

  return (
    <Grid
      container
      spacing={2}
      sx={{
        marginTop: 1,
        marginBottom: 1,
        paddingLeft: 1,
        paddingRight: 1,
      }}
    >
      <ProviderEdit
        control={control}
        files={
          <ProviderFiles
            providerId={existingProvider.id}
            files={existingProvider.files}
            listTemplate={existingProvider.listTemplate}
          />
        }
      />
      <Grid
        size={12}
        container
        sx={{
          justifyContent: "flex-end",
        }}
      >
        <Stack direction="row" spacing={2}>
          <CancelButton onClick={goBack} />
          <Button variant="outlined" color="error" onClick={() => setConfirmationDialogVisible(true)}>
            Usuń
          </Button>
          <AsyncLoadingButton
            onClick={handleSubmit(async (state) => {
              await onUpdateProvider(state as CreateOrUpdateProviderRequest);
            })}
          >
            Zapisz
          </AsyncLoadingButton>
          <ConfirmationDialog
            open={confirmationDialogVisible}
            title="Usuń dostawcę"
            content="Czy chcesz usunąć dostawcę? Tej operacji nie będzie można już cofnąć."
            onCancel={() => setConfirmationDialogVisible(false)}
            onConfirm={handleDelete}
          />
        </Stack>
      </Grid>
    </Grid>
  );
};

const InitializedEditProviderPage = () => {
  const { provider: existingProvider, isLoading, updateProvider, deleteProvider } = useProvider();
  if (isLoading) {
    return (
      <Grid container>
        <Grid size={12}>
          <LinearProgress data-testid="loading-indicator" />
        </Grid>
      </Grid>
    );
  }

  if (!existingProvider) {
    return (
      <Grid
        container
        spacing={2}
        sx={{
          marginTop: 1,
          marginBottom: 1,
        }}
      >
        <Typography
          sx={{
            margin: 2,
          }}
        >
          Taki dostawca nie istnieje
        </Typography>
      </Grid>
    );
  }

  return (
    <EditProviderPage
      existingProvider={existingProvider}
      onUpdateProvider={updateProvider}
      onDeleteProvider={() => deleteProvider(existingProvider.id)}
    />
  );
};

export default InitializedEditProviderPage;
